import { useState, useRef, useEffect, useMemo } from 'react';
import { PLATTS_CATALOG, PLATTS_CATEGORIES, PlattsAssessment, findAssessmentByName } from '../lib/platts-catalog';
import { Stack, Row, Text, Input, Tag } from './index';
import clsx from 'clsx';

type Props = {
  benchmark: string;
  code: string;
  onChange: (b: { benchmark: string; code: string }) => void;
  className?: string;
};

export function PlattsPicker({ benchmark, code, onChange, className }: Props) {
  const [mode, setMode] = useState<'browse' | 'manual'>(() => {
    if (!benchmark) return 'browse';
    return findAssessmentByName(benchmark) ? 'browse' : 'manual';
  });
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const knownMatch = useMemo(() => benchmark ? findAssessmentByName(benchmark) : undefined, [benchmark]);

  const filtered = useMemo(() => {
    const ql = q.toLowerCase().trim();
    return PLATTS_CATALOG.filter(a => {
      if (activeCategory && a.category !== activeCategory) return false;
      if (!ql) return true;
      return (
        a.name.toLowerCase().includes(ql) ||
        a.code?.toLowerCase().includes(ql) ||
        a.category.toLowerCase().includes(ql)
      );
    });
  }, [q, activeCategory]);

  function pick(a: PlattsAssessment) {
    onChange({ benchmark: a.name, code: a.code ?? '' });
    setOpen(false); setQ(''); setActiveCategory(null);
  }

  return (
    <Stack gap={1} className={className}>
      {/* Mode toggle — explicit "browse catalog vs type my own" */}
      <Row gap={0.5}>
        <button
          type="button"
          onClick={() => setMode('browse')}
          className={clsx('flex-1 text-left px-2 py-1.5 rounded-sm hairline text-xs transition-colors',
            mode === 'browse' ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink')}>
          <div className="font-medium">browse catalog</div>
          <div className="text-[10px] opacity-70">140 known assessments</div>
        </button>
        <button
          type="button"
          onClick={() => setMode('manual')}
          className={clsx('flex-1 text-left px-2 py-1.5 rounded-sm hairline text-xs transition-colors',
            mode === 'manual' ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink')}>
          <div className="font-medium">type my own</div>
          <div className="text-[10px] opacity-70">free-text name + code</div>
        </button>
      </Row>

      {mode === 'browse' && (
        <div className="relative" ref={containerRef}>
          <button
            type="button"
            onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 10); }}
            className="w-full h-9 hairline rounded-sm px-2 bg-bg text-base text-left flex items-center justify-between hover:border-ink transition-colors"
          >
            <span className={clsx('truncate', !benchmark && 'text-muted')}>
              {benchmark || 'select benchmark…'}
              {knownMatch?.code && <span className="ml-2 text-xs text-muted tnum">{knownMatch.code}</span>}
            </span>
            <span className="text-muted text-xs ml-1">▾</span>
          </button>

          {open && (
            <div className="absolute left-0 right-0 mt-1 z-30 bg-bg hairline rounded-sm">
              <div className="hairline-b p-2">
                <Input
                  ref={inputRef as any}
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  placeholder="search name, code (e.g. PCAAS00), or category…"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && filtered.length > 0) pick(filtered[0]);
                    else if (e.key === 'Escape') setOpen(false);
                  }}
                />
              </div>
              <div className="px-2 py-1 hairline-b overflow-x-auto whitespace-nowrap">
                <Row gap={0.5}>
                  <button onClick={() => setActiveCategory(null)}
                    className={clsx('h-6 px-1.5 rounded-sm text-xs hairline', !activeCategory ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink')}>all</button>
                  {PLATTS_CATEGORIES.map(c => (
                    <button key={c} onClick={() => setActiveCategory(c === activeCategory ? null : c)}
                      className={clsx('h-6 px-1.5 rounded-sm text-xs hairline whitespace-nowrap', activeCategory === c ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink')}>{c}</button>
                  ))}
                </Row>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="px-3 py-3">
                    <Text variant="small" muted>no match in catalog</Text>
                    <button onClick={() => { setMode('manual'); setOpen(false); }}
                      className="mt-2 text-xs text-ink hover:underline">switch to "type my own" →</button>
                  </div>
                ) : (
                  filtered.slice(0, 80).map(a => (
                    <button key={a.name + (a.code ?? '')} onClick={() => pick(a)}
                      className="w-full text-left px-3 py-1.5 hover:bg-whisper transition-colors">
                      <Row justify="between" align="center">
                        <Stack gap={0.5}>
                          <Text variant="small" className="font-medium">{a.name}</Text>
                          <Text variant="label">{a.category}{a.unit ? ` · ${a.unit}` : ''}</Text>
                        </Stack>
                        {a.code && <span className="text-xs tnum text-muted hairline rounded-sm px-1">{a.code}</span>}
                      </Row>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {mode === 'manual' && (
        <Stack gap={1}>
          <Stack gap={0.5}>
            <Text variant="label">benchmark name</Text>
            <Input
              value={benchmark}
              onChange={e => onChange({ benchmark: e.target.value, code })}
              placeholder='e.g. "ARA Bunker IFO 380" or any custom assessment'
              autoFocus
            />
          </Stack>
          <Stack gap={0.5}>
            <Text variant="label">platts code <span className="text-muted">(optional)</span></Text>
            <Input
              value={code}
              onChange={e => onChange({ benchmark, code: e.target.value.toUpperCase() })}
              placeholder='e.g. AAGZP00 (MOC code or your reference)'
              className="tnum"
            />
          </Stack>
          {benchmark && !knownMatch && <Tag>custom assessment</Tag>}
        </Stack>
      )}
    </Stack>
  );
}
